/**
 * Binding de sessão de consulta ao patientId atual.
 *
 * Motivação (caso Arthur Dias 03/08/2026): NameForm limpava LS/SS ao gerar
 * novo patientId, mas IndexedDB/cookies (sessionBackup) e o gate
 * `hasLegitimateChatAccess` ainda aceitavam `activeChannelId` solto ou
 * `anamneseProntuario` de consulta ANTERIOR — abrindo chat PAGO sem triagem
 * desta sessão (`triageMissing=no_source_available`).
 *
 * Mesmo padrão de `paymentFlag.js` / `chatStartedAtFor`:
 *  - `anamneseProntuario` só vale se `anamneseProntuarioFor === patientId`
 *    e o texto passar em `isValidProntuarioText`;
 *  - `activeChannelId` só vale se o prefixo for `{patientId}-…`.
 *
 * Prontuário SEM `For` (legado pré-deploy) NÃO autoriza abertura de chat:
 * auto-bindaria o texto velho ao patientId novo (contaminando a consulta).
 * Reidratação legítima continua via `/prontuario/cache?patientId=` (servidor).
 */

import { isValidProntuarioText, sanitizeProntuarioText } from './prontuarioText';
import {
  FLOW_NO_TRIAGE_PAID,
  FLOW_VARIANT_KEY,
  FLOW_VARIANT_FOR_KEY,
} from './flowVariant';
import { SILENT_RESET_DONE_KEY } from './silentResetMigration';

export const KEY_ANAMNESE_PRONTUARIO = 'anamneseProntuario';
export const KEY_ANAMNESE_PRONTUARIO_FOR = 'anamneseProntuarioFor';
export const KEY_ACTIVE_CHANNEL_ID = 'activeChannelId';
export const KEY_PRONTUARIO_SENT = 'prontuarioSent';
export const KEY_PAYMENT_SENT = 'paymentSent';

/** Chaves de consulta que NÃO devem sobreviver a um novo patientId. */
export const CONSULTATION_EPHEMERAL_KEYS = [
  'anamnesisHistory',
  KEY_ANAMNESE_PRONTUARIO,
  KEY_ANAMNESE_PRONTUARIO_FOR,
  KEY_PRONTUARIO_SENT,
  KEY_PAYMENT_SENT,
  'finalizedAt',
  KEY_ACTIVE_CHANNEL_ID,
  'appSession',
  'chatStartedAt',
  'chatStartedAtFor',
  'chatLastSeenAt',
  'anamnesisPendingQuestions',
  'anamneseAdditionalPhase',
  'anamneseAdditionalQuestions',
  'anamneseAdditionalIndex',
  'anamneseAdditionalStartIdx',
  'anamneseFastIntakeQueue',
  'anamneseFastIntakePhase',
  'anamneseFastIntakeTried',
  'anamneseDraftInput',
  // A/B de fluxo: braço sorteado e marco de conduta entregue antes do
  // pagamento. Ambos são amarrados ao patientId, então não vazariam de uma
  // consulta para outra. Para o braço `no_triage_paid` a limpeza deixou de ser
  // só higiene: o braço é um dos caminhos de acesso legítimo ao chat
  // (`hasLegitimateChatAccessForPatient`), então deixá-lo para trás autorizaria
  // uma consulta nova a abrir chat sem triagem.
  FLOW_VARIANT_KEY,
  FLOW_VARIANT_FOR_KEY,
  'flowAbConductDeliveredFor',
  // 1 hop de silent reset por sessão paga — não pode vazar para a próxima consulta.
  SILENT_RESET_DONE_KEY,
];

const defaultRead = (key) => {
  try {
    const v = localStorage.getItem(key);
    if (v != null) return v;
  } catch {}
  try {
    const v = sessionStorage.getItem(key);
    if (v != null) return v;
  } catch {}
  return null;
};

const defaultWrite = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
  try {
    sessionStorage.setItem(key, value);
  } catch {}
};

const defaultRemove = (key) => {
  try {
    localStorage.removeItem(key);
  } catch {}
  try {
    sessionStorage.removeItem(key);
  } catch {}
};

/**
 * Canal Stream canônico: `{patientId}-{doctorId}` (ex.: patient-123-abc-DrGuilhermeEsmanhotto).
 * Aceita também `messaging:` prefix do cid Stream.
 */
export function isActiveChannelOwnedBy(patientId, channelId) {
  const pid = String(patientId || '').trim();
  const ch = String(channelId || '').trim();
  if (!pid || !ch) return false;
  const bare = ch.includes(':') ? ch.split(':').pop() : ch;
  return bare === pid || bare.startsWith(`${pid}-`);
}

/**
 * Persiste prontuário + sentinela de dono. Sem patientId → no-op (bug).
 * @returns {boolean}
 */
export function bindAnamneseProntuario(patientId, text, { write = defaultWrite } = {}) {
  const pid = String(patientId || '').trim();
  const sanitized = sanitizeProntuarioText(text);
  if (!pid) return false;
  if (!isValidProntuarioText(sanitized)) return false;
  write(KEY_ANAMNESE_PRONTUARIO, sanitized);
  write(KEY_ANAMNESE_PRONTUARIO_FOR, pid);
  return true;
}

/**
 * Remove prontuário e sentinela (LS+SS via remove).
 */
export function clearAnamneseProntuario({ remove = defaultRemove } = {}) {
  remove(KEY_ANAMNESE_PRONTUARIO);
  remove(KEY_ANAMNESE_PRONTUARIO_FOR);
}

/**
 * Texto do prontuário SOMENTE se amarrado ao patientId e clinicamente válido.
 * Descasamento → limpa as chaves (não herda consulta antiga).
 * Sem `For` (legado) → null (não autoriza; não limpa — reidratação server-side).
 */
export function getBoundAnamneseProntuario(patientId, { get = defaultRead, remove = defaultRemove } = {}) {
  const pid = String(patientId || '').trim();
  if (!pid) return null;
  const raw = get(KEY_ANAMNESE_PRONTUARIO);
  const sanitized = sanitizeProntuarioText(raw);
  if (!isValidProntuarioText(sanitized)) return null;
  const boundTo = String(get(KEY_ANAMNESE_PRONTUARIO_FOR) || '').trim();
  if (!boundTo) return null;
  if (boundTo !== pid) {
    clearAnamneseProntuario({ remove });
    return null;
  }
  return sanitized;
}

/**
 * O braço `no_triage_paid` do A/B de fluxo está atribuído A ESTE patientId?
 *
 * O binding é o mesmo de `anamneseProntuarioFor`: o braço só conta quando
 * `flowAbVariantFor` aponta para o patientId da sessão. Sem isso, o braço de uma
 * consulta anterior no mesmo navegador autorizaria a próxima a abrir chat sem
 * triagem — o caso Arthur Dias, por outro caminho.
 */
export function isNoTriageArmBoundTo(patientId, { get = defaultRead } = {}) {
  const pid = String(patientId || '').trim();
  if (!pid) return false;
  const variant = String(get(FLOW_VARIANT_KEY) || '').trim();
  if (variant !== FLOW_NO_TRIAGE_PAID) return false;
  return String(get(FLOW_VARIANT_FOR_KEY) || '').trim() === pid;
}

/**
 * Critério único do gate anti-bypass (espelha ConsultaChat.hasLegitimateChatAccess).
 *
 * Caminhos legítimos:
 *  1) Canal ativo cujo id pertence ao patientId atual
 *  2) Prontuário válido amarrado ao patientId atual
 *  3) Braço `no_triage_paid` do A/B de fluxo atribuído ao patientId atual
 *
 * O caminho 3 existe porque esse braço entra no chat SEM triagem por desenho: o
 * prontuário dele só nasce das perguntas feitas no chat depois do pagamento, de
 * modo que exigir prontuário aqui trancaria o braço fora do próprio fluxo. Ele
 * não afrouxa o gate para ninguém mais — é a única variante que o satisfaz, e
 * ainda depende do binding ao patientId.
 *
 * `finalizedAt` NÃO autoriza abertura de chat novo — o bootstrap do
 * ConsultaChat já manda para Home quando detecta finalizedAt.
 */
export function hasLegitimateChatAccessForPatient(patientId, { get = defaultRead, remove = defaultRemove } = {}) {
  const pid = String(patientId || '').trim();
  if (!pid) return false;

  const channelId = get(KEY_ACTIVE_CHANNEL_ID);
  if (channelId && isActiveChannelOwnedBy(pid, channelId)) return true;

  // Canal de OUTRO patientId no storage — remove para não “legitimar” depois.
  if (channelId && !isActiveChannelOwnedBy(pid, channelId)) {
    try {
      remove(KEY_ACTIVE_CHANNEL_ID);
    } catch {}
  }

  if (getBoundAnamneseProntuario(pid, { get, remove })) return true;

  return isNoTriageArmBoundTo(pid, { get });
}

/**
 * Limpa chaves efêmeras de consulta em LS/SS (novo patientId / NameForm).
 * Não toca identidade (email/cpf/phone) — isso o caller decide.
 */
export function clearConsultationEphemeralKeys({ remove = defaultRemove } = {}) {
  for (const key of CONSULTATION_EPHEMERAL_KEYS) {
    remove(key);
  }
}
